// "WHAT NEEDS DOING" — the money screen as one flat list of things to act on, each one
// sentence long and each naming what it belongs to (client › retainer › job). It replaces
// opening a retainer, then a job inside it, to find a charge.
//
// THREE SECTIONS, and the split is the whole point:
//   late   a recorded charge the client should have paid already       (red)
//   todo   work that is OURS: a month to bill, a cost to charge on      (amber)
//   owed   a recorded charge that is open and not yet due               (blue)
//
// PURE SHAPING OF THE SERVER'S ROWS. Nothing here computes money: every amount shown is a
// field the server sent, `collectible` and `overdue` are the server's own verdicts, and no
// figure is summed. The headline reads the server's totals, never these lists. Sorting a
// merged list by date is display, which is the browser's to decide.
import { rowAsCard } from './boardRows';

export const SECTIONS = ['late', 'todo', 'owed'];

const recordedItem = (card, row, path) => ({
  key: `inv:${row.invoiceId}`,
  section: row.overdue ? 'late' : 'owed',
  kind: 'recorded',
  card,
  row,
  path,
  when: row.dueDate || '9999',
});

// Oldest first within a section: the longest-waiting thing leads. Ties by key so two rows
// on the same date never swap places between renders.
const byWhen = (a, b) => (a.when === b.when ? (a.key < b.key ? -1 : 1) : (a.when < b.when ? -1 : 1));

export const buildActions = (data) => {
  const out = { late: [], todo: [], owed: [] };
  const push = (item) => out[item.section].push(item);

  for (const r of data.retainers) {
    const path = { client: r.clientName, retainer: r.title, job: null };
    for (const m of r.months) {
      if (m.kind === 'recorded' && m.collectible) push(recordedItem(r, m, path));
      if (m.kind === 'expected_month' && m.gated) {
        push({ key: `month:${r.id}:${m.month}`, section: 'todo', kind: 'bill_month',
          card: r, row: m, month: m.month, path, when: `${m.month}-01` });
      }
    }
    // A retainer invoice recorded with no month sits outside every total — it is a
    // mis-recording to fix, so it is work for us, never a debt on this list.
    // A one-off project (067): its charges rather than months — the price, owed from the
    // day the project exists (068), waiting for payment until collected.
    for (const c of r.charges || []) if (c.collectible) push(recordedItem(r, c, path));
    // A retainer's extra charges (income beyond the monthly fee, no month): owed like any
    // other charge, late or waiting by their own due date.
    for (const x of r.extras || []) if (x.collectible) push(recordedItem(r, x, path));
  }

  for (const j of data.jobs) {
    const path = { client: j.clientName, retainer: j.retainerTitle, job: j.title };
    for (const c of j.charges) if (c.collectible) push(recordedItem(j, c, path));
    for (const e of j.expectedCharges) {
      push({ key: `exp:${e.expenseId}`, section: 'todo', kind: 'charge',
        card: j, row: e, path, when: e.expenseDate || '0000' });
    }
  }

  // A cost to charge on, on a job that does not bill yet. The dialog needs a card-shaped
  // object for the job; boardRows.rowAsCard is the one adapter for that.
  for (const p of data.pendingConversions) {
    push({ key: `exp:${p.expenseId}`, section: 'todo', kind: 'charge',
      card: rowAsCard(p), row: p,
      path: { client: p.clientName, retainer: p.retainerTitle, job: p.jobTitle },
      when: p.expenseDate || '0000' });
  }

  for (const s of SECTIONS) out[s].sort(byWhen);
  return out;
};
